// بخش‌های امنیت «حساب من» (SCR-STAFF-002؛ AP-IAM-03): احراز دومرحله‌ای و کدهای بازیابی (US-IAM-003، BR-IAM-006، 007)، نشست‌های
// فعال با سقف همزمانی (US-IAM-050، BR-IAM-050، 051) و کد تأیید مدیر (PIN) با سقف‌های تأیید عضو (US-IAM-034). 2FA و نشست‌ها با
// توکن Keycloak (سطح کاربر)؛ PIN و سقف‌ها با توکن مستأجر (سطح عضویت).
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type RecoveryCodesResult, type SessionView } from '@phoenix/api-client';
import { useAuth, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  latinDigits,
  LoadingState,
  StatusChip,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ApprovalLimitValue } from './ApprovalLimitsTab';
import { iamKeys } from './common';

const ACCOUNT_RETURN = '/account';

function RecoveryCodesDialog({ result, onClose }: { result: RecoveryCodesResult; onClose: () => void }) {
  const { t } = useTranslation('iam');
  const titleId = useId();
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>{t('iam:account.recovery_title')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('iam:account.recovery_once')}
        </Alert>
        <Box
          component="ol"
          dir="ltr"
          data-testid="recovery-codes"
          sx={{ fontFamily: 'monospace', columns: 2, m: 0, ps: 3 }}
        >
          {result.codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </Box>
        {copied && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('iam:account.recovery_copied')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<ContentCopyOutlinedIcon />}
          onClick={() => {
            void navigator.clipboard?.writeText(result.codes.join('\n')).then(() => setCopied(true));
          }}
        >
          {t('iam:account.copy')}
        </Button>
        <Button variant="contained" onClick={onClose} data-testid="recovery-close">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TwoFactorSection() {
  const { t } = useTranslation('iam');
  const auth = useAuth();
  const { keycloakApi } = useTenantSession();
  const queryClient = useQueryClient();
  const subject = auth.user?.subject;
  const [codes, setCodes] = useState<RecoveryCodesResult | null>(null);
  const [disabling, setDisabling] = useState(false);
  const status = useQuery({
    queryKey: iamKeys.mfa(subject),
    queryFn: () => unwrap(keycloakApi.GET('/api/v1/identity/me/2fa')),
  });
  const generate = useMutation({
    mutationFn: () => unwrap(keycloakApi.POST('/api/v1/identity/me/2fa/recovery-codes')),
    onSuccess: (result) => {
      setCodes(result);
      void queryClient.invalidateQueries({ queryKey: iamKeys.mfa(subject) });
    },
  });
  const disable = useMutation({
    mutationFn: () => unwrap(keycloakApi.DELETE('/api/v1/identity/me/2fa')),
    onSuccess: () => {
      setDisabling(false);
      void queryClient.invalidateQueries({ queryKey: iamKeys.mfa(subject) });
    },
  });
  const data = status.data;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="account-2fa">
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('iam:account.two_factor')}
      </Typography>
      {status.isPending ? (
        <LoadingState rows={2} />
      ) : status.error || !data ? (
        <ErrorState error={status.error} onRetry={() => void status.refetch()} />
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <span data-testid="account-2fa-status" data-enabled={data.enabled}>
              <StatusChip
                tone={data.enabled ? 'active' : 'inactive'}
                labelKey={data.enabled ? 'iam:account.two_factor_on' : 'iam:account.two_factor_off'}
              />
            </span>
            {data.required && (
              <Chip size="small" color="warning" variant="outlined" label={t('iam:account.two_factor_required')} />
            )}
          </Stack>
          <Typography color="text.secondary">{t('iam:account.two_factor_hint')}</Typography>
          {!data.smsAvailable && (
            <Typography variant="body2" color="text.secondary" data-testid="account-2fa-no-sms">
              {t('iam:account.two_factor_no_sms')}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button
              variant={data.enabled ? 'outlined' : 'contained'}
              startIcon={<SecurityOutlinedIcon />}
              onClick={() => void auth.configureTotp(ACCOUNT_RETURN)}
              data-testid="account-configure-totp"
            >
              {t('iam:account.configure_totp')}
            </Button>
            {data.enabled && !data.required && (
              <Button
                color="error"
                onClick={() => {
                  disable.reset();
                  setDisabling(true);
                }}
                data-testid="account-disable-totp"
              >
                {t('iam:account.disable_totp')}
              </Button>
            )}
          </Stack>
          {data.enabled && (
            <Box>
              <Typography variant="subtitle2" component="h3">
                {t('iam:account.recovery_codes')}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography color="text.secondary" data-testid="account-recovery-remaining">
                  {t('iam:account.recovery_remaining', { value: faDigits(data.recoveryCodesRemaining) })}
                </Typography>
                <Button
                  size="small"
                  disabled={generate.isPending}
                  onClick={() => generate.mutate()}
                  data-testid="account-recovery-generate"
                >
                  {t('iam:account.recovery_generate')}
                </Button>
              </Stack>
              {generate.error ? <ErrorState error={generate.error} compact /> : null}
            </Box>
          )}
        </Stack>
      )}
      {codes && <RecoveryCodesDialog result={codes} onClose={() => setCodes(null)} />}
      <ConfirmDialog
        open={disabling}
        titleKey="iam:account.disable_title"
        messageKey="iam:account.disable_message"
        confirmKey="iam:account.disable_totp"
        destructive
        busy={disable.isPending}
        error={disable.error ?? undefined}
        onConfirm={() => disable.mutate()}
        onCancel={() => setDisabling(false)}
      />
    </Paper>
  );
}

function sessionDevice(session: SessionView, unknown: string): string {
  const parts = [session.browser, session.operatingSystem].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : unknown;
}

export function SessionsSection() {
  const { t } = useTranslation('iam');
  const auth = useAuth();
  const { keycloakApi } = useTenantSession();
  const queryClient = useQueryClient();
  const subject = auth.user?.subject;
  const sessions = useQuery({
    queryKey: iamKeys.sessions(subject),
    queryFn: () => unwrap(keycloakApi.GET('/api/v1/identity/me/sessions')),
  });
  const revoke = useMutation({
    mutationFn: (sessionId: string) =>
      unwrap(keycloakApi.DELETE('/api/v1/identity/me/sessions/{sessionId}', { params: { path: { sessionId } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: iamKeys.sessions(subject) }),
  });
  const items = sessions.data?.items ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="account-sessions">
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('iam:account.sessions')}
      </Typography>
      {sessions.data && (
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {t('iam:account.sessions_hint', { value: faDigits(sessions.data.maxSessions) })}
        </Typography>
      )}
      {sessions.isPending ? (
        <LoadingState rows={2} />
      ) : sessions.error ? (
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
      ) : items.length === 0 ? (
        <Typography color="text.secondary">{t('iam:account.sessions_empty')}</Typography>
      ) : (
        <List disablePadding>
          {items.map((session) => (
            <ListItem
              key={session.sessionId}
              divider
              disableGutters
              data-testid={`session-${session.sessionId}`}
              secondaryAction={
                session.current ? (
                  <Chip size="small" color="primary" label={t('iam:account.session_current')} />
                ) : (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<LogoutOutlinedIcon />}
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(session.sessionId)}
                    data-testid={`session-revoke-${session.sessionId}`}
                  >
                    {t('iam:account.session_revoke')}
                  </Button>
                )
              }
              sx={{ pe: 14 }}
            >
              <ListItemText
                primary={sessionDevice(session, t('iam:account.session_unknown_device'))}
                secondary={
                  <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
                    <span>
                      {t('iam:account.session_last')}: <JalaliDate value={session.lastAccessAt} withTime />
                    </span>
                    <span>
                      {t('iam:account.session_started')}: <JalaliDate value={session.startedAt} withTime />
                    </span>
                    {session.ipAddress && (
                      <span>
                        {t('iam:account.session_ip')}: <bdi dir="ltr">{session.ipAddress}</bdi>
                      </span>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {revoke.error ? <ErrorState error={revoke.error} compact /> : null}
    </Paper>
  );
}

const PIN_PATTERN = /^\d{4,8}$/;

/** PIN قابل قبول: ۴ تا ۸ رقم، نه همه یکسان و نه متوالی صعودی یا نزولی (هم‌ارز ApprovalRules.IsAcceptablePin سرور). */
export function isAcceptablePin(pin: string): boolean {
  if (!PIN_PATTERN.test(pin)) {
    return false;
  }
  const digits = [...pin].map(Number);
  const steps = digits.slice(1).map((digit, index) => digit - (digits[index] ?? 0));
  return !steps.every((step) => step === 0) && !steps.every((step) => step === 1) && !steps.every((step) => step === -1);
}

export function ApprovalPinSection() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [pin, setPin] = useState('');
  const [touched, setTouched] = useState(false);
  const [saved, setSaved] = useState(false);
  const mine = useQuery({
    queryKey: iamKeys.myApprovalLimits(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/me/approval-limits')),
  });
  const save = useMutation({
    mutationFn: (value: string) =>
      unwrap(tenantApi.PUT('/api/v1/identity/me/approval-pin', { body: { pin: value } })),
    onMutate: () => setSaved(false),
    onSuccess: () => {
      setPin('');
      setTouched(false);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: iamKeys.myApprovalLimits(session?.tenantId) });
    },
  });
  const value = latinDigits(pin).trim();
  const invalid = !isAcceptablePin(value);

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="account-pin">
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('iam:account.pin')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.5 }}>
        {t('iam:account.pin_hint')}
      </Typography>
      {mine.isPending ? (
        <LoadingState rows={2} />
      ) : mine.error || !mine.data ? (
        <ErrorState error={mine.error} onRetry={() => void mine.refetch()} />
      ) : (
        <Stack spacing={2}>
          <span data-testid="account-pin-status" data-set={mine.data.hasApprovalPin}>
            <StatusChip
              tone={mine.data.hasApprovalPin ? 'active' : 'inactive'}
              labelKey={mine.data.hasApprovalPin ? 'iam:account.pin_set' : 'iam:account.pin_unset'}
            />
          </span>
          {saved && (
            <Alert severity="success" data-testid="account-pin-saved">
              {t('iam:account.pin_saved')}
            </Alert>
          )}
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              setTouched(true);
              if (!invalid) {
                save.mutate(value);
              }
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                type="password"
                label={t('iam:account.pin_new')}
                value={pin}
                onChange={(event) => setPin(event.target.value.slice(0, 8))}
                error={touched && invalid}
                helperText={touched && invalid ? t('errors:validation.approval_pin') : ' '}
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    dir: 'ltr',
                    autoComplete: 'new-password',
                    maxLength: 8,
                    'data-testid': 'account-pin-input',
                  },
                }}
              />
              <Button type="submit" variant="contained" disabled={save.isPending} data-testid="account-pin-save">
                {t('iam:account.pin_save')}
              </Button>
            </Stack>
          </Box>
          {save.error ? <ErrorState error={save.error} compact /> : null}
          <Box>
            <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
              {t('iam:account.my_limits')}
            </Typography>
            <KeyValueList
              items={mine.data.limits.map((limit) => ({
                labelKey: `iam:approval_limits.keys.${limit.key}`,
                value: (
                  <ApprovalLimitValue
                    unit={limit.key.endsWith('percent') ? 'Percent' : 'Amount'}
                    value={limit.value}
                  />
                ),
                testId: `my-limit-${limit.key}`,
              }))}
            />
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
